import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApp, t } from "@/lib/AppContext";
import { api, formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, MessageCircle, Loader2 } from "lucide-react";
import PhoneInput from "./PhoneInput";

export default function FeedbackDialog({ open, onOpenChange, defaultMessage = "", carName = "" }) {
  const { content, settings } = useApp();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [phoneValid, setPhoneValid] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", messenger: "telegram", message: defaultMessage });

  React.useEffect(() => { setForm((f) => ({ ...f, message: defaultMessage })); }, [defaultMessage, open]);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.name.trim() || form.name.trim().length < 2) { toast.error("Введите имя"); return false; }
    if (!phoneValid) { toast.error("Введите номер полностью: +7 и 10 цифр"); return false; }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/applications", {
        type: carName ? "car" : "general",
        name: form.name.trim(),
        phone: form.phone.trim(),
        messenger: form.messenger,
        comment: form.message,
        car_name: carName || undefined,
      });
      onOpenChange(false);
      navigate("/thank-you");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Ошибка отправки");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden border-0" data-testid="feedback-dialog">
        <div className="p-6 md:p-8" style={{ background: "var(--color-bg-primary)" }}>
          <DialogHeader>
            <DialogTitle className="font-headings text-2xl md:text-3xl font-extrabold text-[var(--color-text-primary)]">
              {t(content, "feedback.form_title", "Оставить заявку")}
            </DialogTitle>
            <DialogDescription className="text-[var(--color-text-secondary)] text-sm">
              {t(content, "feedback.form_subtitle", "Менеджер свяжется с вами в течение 15 минут")}
            </DialogDescription>
          </DialogHeader>

          {/* Quick contact buttons */}
          <div className="grid grid-cols-2 gap-3 mt-5">
            {settings?.telegram_url && (
              <a href={settings.telegram_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 px-4 py-3 rounded-[var(--radius-md)] text-white font-accent text-sm font-semibold uppercase tracking-wider" style={{ background: "#229ED9" }} data-testid="feedback-telegram-btn">
                <Send size={16} /> Telegram
              </a>
            )}
            {settings?.whatsapp_url && (
              <a href={settings.whatsapp_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 px-4 py-3 rounded-[var(--radius-md)] text-white font-accent text-sm font-semibold uppercase tracking-wider" style={{ background: "#25D366" }} data-testid="feedback-whatsapp-btn">
                <MessageCircle size={16} /> WhatsApp
              </a>
            )}
          </div>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[var(--color-border)]" />
            <span className="font-accent text-xs uppercase tracking-widest text-[var(--color-text-secondary)]">
              {t(content, "feedback.separator", "или")}
            </span>
            <div className="flex-1 h-px bg-[var(--color-border)]" />
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid="feedback-form">
            <input
              className="field-input"
              type="text"
              placeholder={t(content, "placeholders.name", "Ваше имя")}
              value={form.name}
              onChange={(e) => onChange("name", e.target.value)}
              required
              data-testid="feedback-name-input"
            />
            <PhoneInput
              value={form.phone}
              onChange={(e) => onChange("phone", e.target.value)}
              onValidityChange={setPhoneValid}
              data-testid="feedback-phone-input"
            />
            <div className="flex gap-2 text-sm">
              {[{v: "telegram", l: "Telegram"}, {v: "whatsapp", l: "WhatsApp"}, {v: "phone", l: "Звонок"}].map((m) => (
                <button
                  key={m.v}
                  type="button"
                  onClick={() => onChange("messenger", m.v)}
                  className="flex-1 py-2 rounded-[var(--radius-sm)] font-accent text-xs uppercase tracking-wider transition-colors"
                  style={{
                    background: form.messenger === m.v ? "var(--color-secondary)" : "transparent",
                    color: form.messenger === m.v ? "#FFFFFF" : "var(--color-text-secondary)",
                    border: `1.5px solid ${form.messenger === m.v ? "var(--color-secondary)" : "var(--color-border)"}`,
                  }}
                  data-testid={`messenger-${m.v}`}
                >
                  {m.l}
                </button>
              ))}
            </div>
            <textarea
              className="field-input"
              rows={3}
              placeholder={t(content, "placeholders.comment", "Комментарий")}
              value={form.message}
              onChange={(e) => onChange("message", e.target.value)}
              data-testid="feedback-message-input"
            />
            <button
              type="submit"
              disabled={submitting || !phoneValid || !form.name.trim()}
              className="btn-primary-cta w-full"
              data-testid="feedback-submit"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : t(content, "feedback.submit_btn", "Отправить заявку")}
            </button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
